import { CommonModule } from "@angular/common";
import { ChangeDetectionStrategy, Component, computed, effect, inject, input, signal, untracked } from "@angular/core";
import { IProjectStageDto } from "src/models/new-dwelling/new-dwelling.model";
import { NewDwellingDataService } from "src/app/infopage/services/new-dwelling-data.service";
import { NewDwellingSalesDataService } from "src/services/new-dwellings/new-dwelling-sales-data.service";
import { StageHeaderComponent } from "src/app/shared-components/new-dwelling/stage-header/stage-header.component";
import { ProjectStageSelectorComponent } from "src/app/shared-components/new-dwelling/project-stage-selector/project-stage-selector.component";
import { OptionsObject } from "src/app/shared-components/ev-dropdown/ev-dropdown.component";
import { ImagePackage } from "src/models/common/image.model";
import { ImageCarouselComponent } from "src/app/shared-components/image-carousel/image-carousel.component";
import { EvCircularProgressComponent } from "src/app/shared-components/ev-circular-progress/ev-circular-progress.component";
import { KeyInfoProjectComponent } from "src/app/shared-components/new-dwelling/key-info-project/key-info-project.component";
import { InfopageLinksNewDwellingComponent } from "src/app/shared-components/new-dwelling/infopage-links/infopage-links.component";
import { ProjectStagesCompactComponent } from "src/app/shared-components/new-dwelling/project-stages-compact/project-stages-compact.component";
import { StatisticsCompactComponent } from "src/app/shared-components/new-dwelling/statistics-compact/statistics-compact.component";
import { UnitsGroupsComponent } from "./units-groups/units-groups.component";
import { ConvertUtil } from "src/util/tools/convert.util";
import { MapActionsService } from "src/services/map/map-actions.service";

@Component({
    selector: "new-dwelling-stage-infocard",
    imports: [
        CommonModule,
        StageHeaderComponent,
        ProjectStageSelectorComponent,
        ImageCarouselComponent,
        EvCircularProgressComponent,
        KeyInfoProjectComponent,
        InfopageLinksNewDwellingComponent,
        ProjectStagesCompactComponent,
        StatisticsCompactComponent,
        UnitsGroupsComponent,
    ],
    templateUrl: "./new-dwelling-stage-infocard.component.html",
    styleUrl: "./new-dwelling-stage-infocard.component.scss",
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NewDwellingStageInfocardComponent {
    projectStage = input.required<IProjectStageDto>();

    private readonly newDwellingSalesDataService = inject(NewDwellingSalesDataService);
    private readonly newDwellingDataService = inject(NewDwellingDataService);
    private readonly mapActionsService = inject(MapActionsService);

    projectData = this.newDwellingDataService.projectData;
    _imagePackages = this.newDwellingDataService.imagePackages;
    publications = this.newDwellingDataService.publications;
    unitsFilterGroups = this.newDwellingDataService.unitsFilterGroups;

    selectedProjectStage = signal<number>(-1);
    stageMode = signal<boolean>(false);

    public readonly colorGreen15 = ["#A7DD74", "#65983D"];
    public readonly colorBlue20 = ["#3d91c6", "#115389"];

    imagePackages = computed(() => {
        const packages = this._imagePackages();
        if (this.stageMode()) {
            return packages;
        } else {
            return this.pickMainImage(packages);
        }
    });

    hasImages = computed(() => {
        return this._imagePackages().some((imagePackage: ImagePackage) => imagePackage.images?.length > 0);
    });

    estateTypeCssClass = computed(() => ConvertUtil.estateTypesToCssClass(this.projectStage()?.estateTypes ?? []));

    segments = computed(() => {
        const stages = this.projectData()?.projectInfo?.stageEstateTypesModel?.projectStageEstateTypes ?? [];
        return stages.map((stage) => ({
            value: stage.numberSold ?? 0,
            color: stage.numberSold === stage.numUnits ? this.colorGreen15 : this.colorBlue20,
        }));
    });

    percentSold = computed(() => {
        const project = this.projectData()?.projectInfo;
        if (project?.numUnits && project.numUnits > 0) {
            return Math.round(((project.numberSold ?? 0) / project.numUnits) * 100);
        }
        return 0;
    });

    constructor() {
        effect(() => {
            const stage = this.projectStage();
            // Open a preselected stage (e.g. from the URL), otherwise default to the project overview ("Prosjektoversikt", key -1).
            const stageEstateTypeId = untracked(() => this.newDwellingSalesDataService.activeStageEstateTypeId());
            this.newDwellingDataService.setProjectId(stage.projectId);
            this.newDwellingDataService.setProjectStageEstateTypeId(stageEstateTypeId ?? null);
            this.selectedProjectStage.set(stageEstateTypeId ?? -1);
            this.stageMode.set(!!stageEstateTypeId);
        });
    }

    centerMap(): void {
        this.mapActionsService.centerOn({ lat: this.projectStage().latitude, lng: this.projectStage().longitude });
    }

    showInMap(): void {
        this.mapActionsService.makeOutline({ lat: this.projectStage().latitude, lng: this.projectStage().longitude });
    }

    close(): void {
        this.selectedProjectStage.set(null);
        this.newDwellingSalesDataService.setSelectedStage(null);
        this.newDwellingDataService.setProjectId(null);
        this.newDwellingDataService.setProjectStageEstateTypeId(null);
    }

    onSelect(event: OptionsObject) {
        if (event.key !== -1) {
            this.newDwellingDataService.setProjectStageEstateTypeId(event.key as number);
            this.selectedProjectStage.set(event.key as number);
            this.stageMode.set(true);
        } else {
            this.newDwellingDataService.setProjectStageEstateTypeId(null);
            this.stageMode.set(false);
            this.selectedProjectStage.set(-1);
        }
    }

    /**
     * Temporary method implementation
     *
     * When "main iamge" becomes avaliable in data, modify this method
     */
    private pickMainImage(imagePackages: ImagePackage[]): ImagePackage[] {
        const source = imagePackages.find((pkg) => pkg.images?.length > 0);
        if (!source) return [];
        return [{ ...source, images: [source.images[0]] }];
    }
}
